import { cn } from '@/lib/cn'

/** A pulsing placeholder block. Use where the content's shape is known (design §10). */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-sm bg-surface-3', className)} />
}

/** Placeholder rows for a table body while data loads. A phone shows the first three columns
 *  (a 320 px row cannot hold eight bars), a tablet five, a desktop all of them. */
export function TableSkeleton({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="flex flex-col gap-2 p-3" role="status" aria-label="Загрузка">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex gap-3">
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton
              key={c}
              className={cn('h-5', c === 0 ? 'w-1/4' : 'flex-1', c >= 3 && 'hidden sm:block', c >= 5 && 'sm:hidden lg:block')}
            />
          ))}
        </div>
      ))}
    </div>
  )
}

/** KPI tiles while the numbers load: the same grid and height as StatCard. */
export function StatSkeleton({ count = 4, className }: { count?: number; className?: string }) {
  return (
    <div
      className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}
      role="status"
      aria-label="Загрузка"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex h-24 flex-col gap-3 rounded-md border border-hairline bg-surface-1 p-4">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      ))}
    </div>
  )
}

/** A form while its values load: label and field pairs. */
export function FormSkeleton({ fields = 5, className }: { fields?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-4', className)} role="status" aria-label="Загрузка">
      {Array.from({ length: fields }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-11 w-full" />
        </div>
      ))}
    </div>
  )
}
